<template>
  <VueFinalModal
    :esc-to-close="true"
    class="flex justify-center items-center"
    content-class="relative w-full h-full"
    overlay-class="!bg-transparent"
  >
    <div v-if="!apiToken" class="token-loading-overlay">
      <div class="token-loading-spinner"></div>
    </div>
    <KestrelLocationSelector
      :open="!!apiToken"
      :country-iso3="countryIso3"
      :api-base-url="sparrowBaseUrl"
      :api-key="apiToken"
      :center="initialCenter"
      :current-lat-lng="initialCenter"
      @select="onSelect"
      @close="closeModal"
    />
  </VueFinalModal>
</template>

<script setup>
import { computed, onMounted } from 'vue'
import { VueFinalModal } from 'vue-final-modal'
import { KestrelLocationSelector } from '@ishare/kestrel-vue'
import '@ishare/kestrel-vue/style.css'
import 'maplibre-gl/dist/maplibre-gl.css'
import FlashMessageService from '@services/FlashMessages'
import { useSparrowAuth } from '@composables/useSparrowAuth'
import GlobalEventBus from '../services/GlobalEventBus'

const props = defineProps({
  params: {
    type: Object,
    required: true,
  },
})

const emit = defineEmits(['close'])

const initialLocation = computed(() => props.params?.initialLocation || {})
const kestrelApiUrl = computed(() => props.params?.kestrelApiUrl || '')

const countryIso3 = computed(() => initialLocation.value?.countryCode || null)

const sparrowBaseUrl = computed(() => kestrelApiUrl.value.replace(/\/api\/?$/, ''))

const initialCenter = computed(() => {
  const lat = initialLocation.value?.lat
  const lng = initialLocation.value?.lng
  if (lat != null && lng != null) return [parseFloat(lng), parseFloat(lat)]
  return null
})

const { token: apiToken, fetchToken } = useSparrowAuth({
  countryIso3,
  capabilities: ['boundary.view:all'],
})

onMounted(async () => {
  if (!countryIso3.value) return
  try {
    await fetchToken()
  } catch (err) {
    console.error('Failed to fetch location selector token:', err)
    FlashMessageService.error('Failed to load map. Please try again.')
  }
})

function onSelect(payload) {
  const eventPayload = {
    attribution: 'imw_point_selection',
  }

  if (payload.lat != null && payload.lng != null) {
    eventPayload.lat = payload.lat
    eventPayload.lng = payload.lng
  }

  GlobalEventBus.dispatch('location-saved', eventPayload)
  closeModal()
}

function closeModal() {
  emit('close')
}
</script>

<style scoped>
.token-loading-overlay {
  position: fixed;
  inset: 0;
  z-index: 50;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.5);
}

.token-loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid rgba(255, 255, 255, 0.3);
  border-top-color: #ffffff;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
